import React, {useState} from 'react';
import Styled from 'styled-components';
import {Chip, FlexColumn, Text} from '@forecast-it/design-system';
import {useIntl} from 'react-intl';
import {BG_NEUTRAL_2, BORDER_DECORATIVE} from './colors';

export interface ResolutionCalloutProps {
	question: string;
	candidates: string[];
	onResolve: (answer: string) => void;
}

// background/neutral/2 with a 1px border/decorative stroke.
const Callout = Styled.div`
	padding: 12px;
	border-radius: 8px;
	background: ${BG_NEUTRAL_2};
	border: 1px solid ${BORDER_DECORATIVE};
	margin-top: 8px;
`;

const ChipRow = Styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 8px;
	margin-top: 8px;
`;

const ResolutionCallout = ({question, candidates, onResolve}: ResolutionCalloutProps): JSX.Element => {
	const {formatMessage} = useIntl();
	// Guards against a double-click resolving the same interrupt twice: the parent removes this
	// callout asynchronously (its own re-render), so a second click landing before that re-render
	// commits would otherwise call onResolve again for a graph interrupt already resumed once.
	const [resolved, setResolved] = useState(false);
	const resolve = (answer: string) => {
		if (resolved) return;
		setResolved(true);
		onResolve(answer);
	};
	// TNSR-635 review (Animesh's manual QA): the agent's own `candidates` sometimes already include
	// "Skip" as one of the offered answers, which — stacked next to the Chip this component always
	// appends below — rendered two visually identical "Skip" chips. Filtered out here rather than
	// relying on the caller to pre-sanitize the agent's payload.
	const dedupedCandidates = candidates.filter(candidate => candidate.trim().toLowerCase() !== 'skip');
	return (
		<Callout data-cy="resolution-callout">
			<FlexColumn gap="xs">
				<Text>{question}</Text>
				<ChipRow>
					{dedupedCandidates.map(candidate => (
						<Chip
							key={candidate}
							disabled={resolved}
							onSelect={() => resolve(candidate)}
							data-cy={`callout-candidate-${candidate}`}
						>
							{candidate}
						</Chip>
					))}
					<Chip disabled={resolved} onSelect={() => resolve('Skip')} data-cy="callout-skip">
						{formatMessage({id: 'common.skip'})}
					</Chip>
				</ChipRow>
			</FlexColumn>
		</Callout>
	);
};

export default ResolutionCallout;
